import { CalendarTimeEntry, CalendarMonthData } from '../../services/types';
import {
  formatHours,
  getCompanyColor,
  formatDateKey,
  normalizeCompanyName,
  getTotalHours,
  getWeekDates,
  getWorkWeekDates,
  dayNames,
  isSameDay,
} from './calendarUtils';
import { ViewMode } from './ViewSelector';

interface WeekViewProps {
  currentDate: Date;
  setCurrentDate: (date: Date) => void;
  monthData: CalendarMonthData | null;
  selectedCompanies: Set<string>;
  searchFilter?: (entry: CalendarTimeEntry) => boolean;
  onEntryClick: (entry: CalendarTimeEntry, event: React.MouseEvent) => void;
  onAddEntry: (date: Date) => void;
  viewMode: ViewMode; // 'week5' or 'week7'
  setViewMode: (mode: ViewMode) => void;
  // Drag-and-drop (optional — wired from Calendar.tsx)
  dragEntryId?: string | null;
  dropTargetDate?: string | null;
  onEntryDragStart?: (entry: CalendarTimeEntry, e: React.DragEvent) => void;
  onEntryDragEnd?: () => void;
  onDayDragOver?: (dateStr: string, e: React.DragEvent) => void;
  onDayDragLeave?: () => void;
  onDayDrop?: (dateStr: string, e: React.DragEvent) => void;
}

export default function WeekView({
  currentDate,
  setCurrentDate,
  monthData,
  selectedCompanies,
  searchFilter,
  onEntryClick,
  onAddEntry,
  viewMode,
  setViewMode,
  dragEntryId,
  dropTargetDate,
  onEntryDragStart,
  onEntryDragEnd,
  onDayDragOver,
  onDayDragLeave,
  onDayDrop,
}: WeekViewProps) {
  const is7Day = viewMode === 'week7';
  const weekDates = is7Day ? getWeekDates(currentDate) : getWorkWeekDates(currentDate);
  const today = new Date();

  const getEntriesForDate = (date: Date): CalendarTimeEntry[] => {
    if (!monthData?.byDate) return [];
    const key = formatDateKey(date);
    const entries = monthData.byDate[key] || [];
    if (selectedCompanies.size === 0) return [];
    return entries
      .filter(e => selectedCompanies.has(normalizeCompanyName(e.companyName)))
      .filter(e => !searchFilter || searchFilter(e));
  };

  // Calculate total hours for the week
  const weekTotalHours = weekDates.reduce((sum, date) => {
    return sum + getTotalHours(getEntriesForDate(date));
  }, 0);

  // Handle clicking on day header to switch to day view
  const handleDayHeaderClick = (date: Date) => {
    setCurrentDate(date);
    setViewMode('day');
  };

  return (
    <div className="flex flex-col h-full">
      {/* Week Header with total */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-slate-700 bg-slate-800/50">
        <div className="text-sm text-slate-400">
          Week of {weekDates[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {weekDates[weekDates.length - 1].toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
        </div>
        <div className="text-sm text-slate-300">
          Total: <span className="font-medium text-white">{formatHours(weekTotalHours)}</span>
        </div>
      </div>

      {/* Days Grid */}
      <div className={`flex-1 grid ${is7Day ? 'grid-cols-7' : 'grid-cols-5'} min-h-0`}>
        {weekDates.map((date, index) => {
          const entries = getEntriesForDate(date);
          const dayHours = getTotalHours(entries);
          const isToday = isSameDay(date, today);
          const dayIndex = date.getDay();
          const dateKey = formatDateKey(date);
          const isDropTarget = !!dropTargetDate && dropTargetDate === dateKey && dragEntryId != null;

          return (
            <div
              key={index}
              className={`flex flex-col min-h-0 ${
                index < weekDates.length - 1 ? 'border-r border-slate-700' : ''
              } ${isDropTarget ? 'ring-2 ring-inset ring-blue-500 bg-blue-900/20' : ''}`}
              onDragOver={onDayDragOver ? (e) => onDayDragOver(dateKey, e) : undefined}
              onDragLeave={onDayDragLeave}
              onDrop={onDayDrop ? (e) => onDayDrop(dateKey, e) : undefined}
            >
              {/* Day Column Header */}
              <button
                onClick={() => handleDayHeaderClick(date)}
                className="flex-shrink-0 p-2 border-b border-slate-700 hover:bg-slate-700/50 transition-colors text-center"
              >
                <div className="text-[10px] text-slate-500 uppercase tracking-wide">{dayNames[dayIndex]}</div>
                <div className="flex flex-col items-center mt-1">
                  <span className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-medium ${
                    isToday ? 'bg-blue-600 text-white' : 'text-slate-300'
                  }`}>
                    {date.getDate()}
                  </span>
                  <span className="text-[10px] text-slate-500 mt-0.5">
                    {dayHours > 0 ? (
                      <>{formatHours(dayHours)} <span className="text-slate-600">#{entries.length}</span></>
                    ) : '\u00A0'}
                  </span>
                </div>
              </button>

              {/* Entries Column - click blank space to add entry */}
              <div
                className="flex-1 overflow-y-auto p-1 space-y-1 cursor-pointer hover:bg-slate-800/30 transition-colors"
                onClick={() => onAddEntry(date)}
              >
                {entries.map((entry, entryIndex) => {
                  const color = getCompanyColor(entry.companyName);
                  const isDragging = dragEntryId != null && dragEntryId === entry.id;
                  return (
                    <button
                      key={entry.id || entryIndex}
                      onClick={(e) => { e.stopPropagation(); onEntryClick(entry, e); }}
                      draggable={!!onEntryDragStart}
                      onDragStart={onEntryDragStart ? (e) => { e.stopPropagation(); onEntryDragStart(entry, e); } : undefined}
                      onDragEnd={onEntryDragEnd}
                      className={`w-full text-left px-2 py-1 rounded-md bg-slate-700/60 hover:bg-slate-700 border-l-2 transition-colors ${onEntryDragStart ? 'cursor-grab active:cursor-grabbing' : ''} ${isDragging ? 'opacity-40' : ''}`}
                      style={{ borderLeftColor: color.hex }}
                      title={onEntryDragStart ? `${entry.companyName}: ${formatHours(entry.hoursWorked)} — drag to move/copy` : undefined}
                    >
                      <div className="text-[11px] font-medium text-slate-300 truncate">{entry.companyName}</div>
                      <div className="text-[10px] text-slate-500">{formatHours(entry.hoursWorked)}</div>
                    </button>
                  );
                })}
                {entries.length === 0 && (
                  <div className="flex items-center justify-center h-full min-h-[60px] text-slate-600 text-xs">
                    —
                  </div>
                )}
              </div>

              {/* Add Entry Button */}
              <div className="flex-shrink-0 p-1">
                <button
                  onClick={() => onAddEntry(date)}
                  className="w-full py-1 text-slate-500 hover:text-blue-400 hover:bg-slate-700/50 rounded transition-colors"
                  title="Add entry"
                >
                  <svg className="w-4 h-4 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
